import { Dispatch, FC, SetStateAction, useEffect, useState } from 'react';
import { v4 as uuid } from 'uuid';
import { useAuth0 } from '@auth0/auth0-react';
import { usePostHog } from '@posthog/react';
import { useAppSelector } from '../../redux/hooks';
import { selectUser } from '../../redux/reducers/userSlice';
import { useLearnosityBffInitialization } from '../../custom-hooks/useLearnosityBffInitialization';
import { LearnosityRequest } from '../../models/LearnosityRequest';
import { assessmentRegions } from '../../learnosity-regions/AssessmentRegions';
import { ThreeDotLoader } from '../../components/Fallback/ThreeDotsLoader';
import { PreSkillChallenge } from '../../components/SkillResource/PreSkillChallenge';
import AssessmentWrapper from '../../components/AssessmentWrapper/AssessmentWrapper';
import { AssessmentCompleted } from '../../components/AssessmentCompleted/AssessmentCompleted';
import { ChallengePhase } from '../../components/AssessmentComponent/AssessmentComponent';
import { AttemptInformation } from '../../models/AttemptInformation';
import { SkillResourceSteps } from '../../models/Timeline';
import { noop } from '../../utils/noop';
import { PreviewModeProvider } from '../../contexts/PreviewModeContext';
import { isAbortError } from '../../utils/fetchCancellation';

interface Props {
  skillCode: string;
  title: string;
  assessmentInProgress: boolean;
  setCurrentStep: Dispatch<SetStateAction<SkillResourceSteps | null>>;
  setAssessmentInProgress: Dispatch<SetStateAction<boolean>>;
}

export const DemoSkillChallenge: FC<Props> = ({
  skillCode,
  title,
  assessmentInProgress,
  setCurrentStep,
  setAssessmentInProgress,
}) => {
  const user = useAppSelector(selectUser);
  const { getAccessTokenSilently, logout } = useAuth0();
  const posthog = usePostHog();
  const { fetchInitialization, refetchInitialization } = useLearnosityBffInitialization(
    getAccessTokenSilently,
    logout,
    posthog,
  );
  const [initObj, setInitObj] = useState<LearnosityRequest | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [retryKey, setRetryKey] = useState(0);
  const [phase, setPhase] = useState<ChallengePhase>('PRE');
  const [attemptInformation, setAttemptInformation] = useState<AttemptInformation | null>(null);

  useEffect(() => {
    if (!user?.sub) return;
    const controller = new AbortController();
    setLoadFailed(false);
    setInitObj(null);
    setAttemptInformation(null);

    fetchInitialization(
      {
        userId: user.sub,
        state: 'initial',
        sessionId: uuid(),
        activityId: `JUMPSTART_PROVE_IT_${skillCode}_0`,
        regions: assessmentRegions,
        title,
        renderingType: 'assess',
        showCorrectAnswers: false,
        autoSaveAnswers: false,
        responsiveRegions: false,
        showIntro: false,
        scrollToTest: false,
        scrollToTop: false,
      },
      { signal: controller.signal },
    )
      .then(data => {
        if (!controller.signal.aborted) setInitObj(data);
      })
      .catch(err => {
        if (controller.signal.aborted || isAbortError(err)) return;
        console.log('DemoSkillChallenge: getLearnosityInitObject failed', err);
        setLoadFailed(true);
      });
    return () => controller.abort();
  }, [skillCode, title, user?.sub, retryKey]);

  return (
    <PreviewModeProvider>
      {assessmentInProgress && phase === 'PRE' && <PreSkillChallenge onFinish={() => setPhase('ASSESSMENT')} />}
      <div data-testid="demo-skill-challenge" className="tw-relative tw-w-full nc_block nc_learnosity">
        {attemptInformation ? (
          <AssessmentCompleted
            attemptInformation={attemptInformation}
            setAttemptInformation={setAttemptInformation}
            previousAttempts={[]}
            incompleteEssentialSkills={[]}
            workedExampleUrl={null}
            sssAfterTakeAssessment={{ previousState: null, nextState: null }}
            setReviewDialogVisible={noop}
            onMoveForward={() => {
              setAssessmentInProgress(false);
              setCurrentStep(SkillResourceSteps.ABOUT_THE_SKILL);
            }}
            setCurrentStep={setCurrentStep}
            refetchLrnSession={noop}
            skipNewActivityId
            setSkipNewActivityId={noop}
            setAssessmentInProgress={setAssessmentInProgress}
            setPhase={setPhase}
            // The demo has no real skill state, so no prior credit to suppress the weekly goal.
            preAttemptSubstateId={null}
          />
        ) : loadFailed ? (
          <div data-testid="demo-skill-challenge-error" className="tw-flex tw-items-center tw-gap-2 tw-p-1">
            <span>Couldn’t load the Skill Challenge.</span>
            <button
              type="button"
              onClick={() => setRetryKey(k => k + 1)}
              className="tw-underline tw-text-blue-700 tw-cursor-pointer tw-bg-transparent tw-border-0"
            >
              Try again
            </button>
          </div>
        ) : initObj ? (
          <AssessmentWrapper
            request={initObj}
            refetchInitialization={refetchInitialization}
            onFailure={() => setLoadFailed(true)}
            sessionId={initObj.request.session_id}
            setAttemptInfo={setAttemptInformation}
            setAlreadySubmitted={noop}
            setCurrentStep={setCurrentStep}
            setAssessmentInProgress={setAssessmentInProgress}
            changeState={noop}
            setLoading={noop}
            setCurrentStatus={noop}
            previousAttempts={[]}
            setPreviousAttempts={noop}
            setPreviousStatus={noop}
            setDisabledReviewModal={noop}
          />
        ) : (
          <ThreeDotLoader dark />
        )}
      </div>
    </PreviewModeProvider>
  );
};
